import apiClient from '@/shared/api/client'

const BASE = '/bale-mgmt'

// ── Mill Lot auto-fill (THE KEY FUNCTION) ─────────────────────────────────────
export const millLotApi = {
  /** Auto-fill lookup: enter mill_lot_no → get all linked data */
  lookup: (code: string) => apiClient.get(`${BASE}/mill-lots/lookup`, { params: { code } }).then(r => r.data),
  list: (params?: Record<string, unknown>) => apiClient.get(`${BASE}/mill-lots`, { params }).then(r => r.data),
  get: (id: string) => apiClient.get(`${BASE}/mill-lots/${id}`).then(r => r.data),
  create: (data: Record<string, unknown>) => apiClient.post(`${BASE}/mill-lots`, data).then(r => r.data),
  update: (id: string, data: Record<string, unknown>) => apiClient.patch(`${BASE}/mill-lots/${id}`, data).then(r => r.data),
  createFromGrn: (grn_id: string) => apiClient.post(`${BASE}/mill-lots/from-grn/${grn_id}`).then(r => r.data),
  refreshStats: (lot_id: string) => apiClient.post(`${BASE}/mill-lots/${lot_id}/refresh-stats`).then(r => r.data),
  archive: (lot_id: string, force = false) =>
    apiClient.post(`${BASE}/mill-lots/${lot_id}/archive`, null, { params: { force } }).then(r => r.data),
  getBales: (lot_id: string) => apiClient.get(`${BASE}/mill-lots/${lot_id}/bales`).then(r => r.data),
  bulkHvi: (lot_id: string, rows: Record<string, unknown>[]) =>
    apiClient.post(`${BASE}/mill-lots/${lot_id}/bulk-hvi`, { rows }).then(r => r.data),
}

// ── HVI Results ───────────────────────────────────────────────────────────────
export const hviApi = {
  history: (bale_id: string) => apiClient.get(`${BASE}/bales/${bale_id}/hvi`).then(r => r.data),
  enter: (bale_id: string, data: Record<string, unknown>) => apiClient.post(`${BASE}/bales/${bale_id}/hvi`, data).then(r => r.data),
  approve: (result_id: string, data: Record<string, unknown>) =>
    apiClient.post(`${BASE}/hvi/${result_id}/approve`, data).then(r => r.data),
}

// ── Laydown Plans ─────────────────────────────────────────────────────────────
export const laydownApi = {
  list: (params?: Record<string, unknown>) => apiClient.get(`${BASE}/laydowns`, { params }).then(r => r.data),
  get: (id: string) => apiClient.get(`${BASE}/laydowns/${id}`).then(r => r.data),
  create: (data: Record<string, unknown>) => apiClient.post(`${BASE}/laydowns`, data).then(r => r.data),
  addBale: (id: string, bale_id: string) =>
    apiClient.post(`${BASE}/laydowns/${id}/add-bale`, { bale_id }).then(r => r.data),
  removeBale: (ld_id: string, line_id: string) =>
    apiClient.delete(`${BASE}/laydowns/${ld_id}/lines/${line_id}`).then(r => r.data),
  submit: (id: string) => apiClient.post(`${BASE}/laydowns/${id}/submit`).then(r => r.data),
  approve: (id: string, data?: Record<string, unknown>) => apiClient.post(`${BASE}/laydowns/${id}/approve`, data || {}).then(r => r.data),
  release: (id: string) => apiClient.post(`${BASE}/laydowns/${id}/release`).then(r => r.data),
  cancel: (id: string) => apiClient.post(`${BASE}/laydowns/${id}/cancel`).then(r => r.data),
  startExecution: (id: string) =>
    apiClient.post(`${BASE}/laydowns/${id}/start-execution`).then(r => r.data),
  startConditioning: (id: string, data: { conditioning_days: number; pre_godown_location?: string }) =>
    apiClient.post(`${BASE}/laydowns/${id}/start-conditioning`, data).then(r => r.data),
  releaseConditioning: (id: string, data: { force_reason?: string }) =>
    apiClient.post(`${BASE}/laydowns/${id}/release-conditioning`, data).then(r => r.data),
  conditioningQueue: (plant_id?: string) =>
    apiClient.get(`${BASE}/conditioning-queue`, { params: plant_id ? { plant_id } : {} }).then(r => r.data),
  byStatus: (statuses: string, plant_id?: string) =>
    apiClient.get(`${BASE}/laydowns-by-status`, { params: { statuses, plant_id } }).then(r => r.data),
}

// ── Executions ────────────────────────────────────────────────────────────────
export const executionApi = {
  list: (params?: Record<string, unknown>) => apiClient.get(`${BASE}/executions`, { params }).then(r => r.data),
  get: (id: string) => apiClient.get(`${BASE}/executions/${id}`).then(r => r.data),
  scan: (id: string, data: Record<string, unknown>) =>
    apiClient.post(`${BASE}/executions/${id}/scan`, data).then(r => r.data),
  complete: (id: string, comments?: string) =>
    apiClient.post(`${BASE}/executions/${id}/complete`, null, { params: { comments } }).then(r => r.data),
}

// ── Chute Feed ────────────────────────────────────────────────────────────────
export const chuteFeedApi = {
  list: (params?: Record<string, unknown>) => apiClient.get(`${BASE}/chute-feed`, { params }).then(r => r.data),
  add: (data: Record<string, unknown>) => apiClient.post(`${BASE}/chute-feed`, data).then(r => r.data),
  // Sprint 0071 — laydowns in active states (RELEASED / CONDITIONING /
  // READY_FOR_MIX / MIXING_IN_PROGRESS) — populates the "Active Laydown"
  // dropdown so completed plans don't pollute the list.
  activeLaydowns: (plant_id?: string) =>
    apiClient.get(`${BASE}/chute-feed/active-laydowns`, {
      params: plant_id ? { plant_id } : {},
    }).then(r => r.data),
  // Sprint 0071 — upload a JPG / PDF / Excel HVI report and get one
  // parsed sample row back (with confidence). Reuses the GRN OCR pipe.
  parseHvi: (data: { file_name: string; file_data: string }) =>
    apiClient.post(`${BASE}/chute-feed/parse-hvi`, data).then(r => r.data),
}

// ── Sprint 0073 — Card Production spine (Cans + standards + IDT) ─────────────
export const cansApi = {
  list: (params?: Record<string, unknown>) =>
    apiClient.get(`${BASE}/cans`, { params }).then(r => r.data),
  get: (id: string) =>
    apiClient.get(`${BASE}/cans/${id}`).then(r => r.data),
  trace: (id: string) =>
    apiClient.get(`${BASE}/cans/${id}/trace`).then(r => r.data),
  updateQc: (id: string, body: { new_status: string; remark?: string | null }) =>
    apiClient.patch(`${BASE}/cans/${id}/qc-status`, body).then(r => r.data),
  bulkUpdateQc: (body: { can_ids: string[]; new_status: string; remark?: string | null }) =>
    apiClient.patch(`${BASE}/cans/bulk/qc-status`, body).then(r => r.data),
  recalcCost: (id: string) =>
    apiClient.post(`${BASE}/cans/${id}/recalc-cost`).then(r => r.data),
  validateForIdt: (body: { can_ids: string[]; allow_pending_with_override?: boolean }) =>
    apiClient.post(`${BASE}/cans/validate-for-idt`, body).then(r => r.data),
}

export const idtFromCansApi = {
  create: (body: {
    can_ids: string[]
    plant_id?: string
    from_dept_id?: string
    to_dept_id?: string
    from_stage?: string
    to_stage?: string
    material_type?: string
    remarks?: string
    allow_pending_with_override?: boolean
  }) => apiClient.post(`${BASE}/idt/from-cans`, body).then(r => r.data),
}

export const cardProductionApi = {
  forecast: (params: {
    plant_id: string
    machine_id?: string
    count_id?: string
    running_hours?: number
    actual_efficiency_pct?: number
    actual_output_kg?: number
  }) => apiClient.get(`${BASE}/card-production/forecast`, { params }).then(r => r.data),
  listStandards: (params: { plant_id: string; machine_id?: string; count_id?: string }) =>
    apiClient.get(`${BASE}/card-production/standards`, { params }).then(r => r.data),
  upsertStandard: (body: Record<string, unknown>) =>
    apiClient.post(`${BASE}/card-production/standards`, body).then(r => r.data),
}

export const wipValuationApi = {
  get: (params: { plant_id: string; department?: string; laydown_id?: string; qc_status?: string }) =>
    apiClient.get(`${BASE}/wip-valuation`, { params }).then(r => r.data),
}

// ── Sprint 0074 — Daily checklist + QC observation logs ──────────────────────
export const checklistLogsApi = {
  list: (params?: Record<string, unknown>) =>
    apiClient.get(`${BASE}/checklist-logs`, { params }).then(r => r.data),
  upsert: (body: Record<string, unknown>) =>
    apiClient.post(`${BASE}/checklist-logs`, body).then(r => r.data),
}


// ── Sprint 0072 — Laydown Reconciliation (issued vs consumed) ────────────────
export const reconciliationApi = {
  pending: (plant_id?: string) =>
    apiClient.get(`${BASE}/reconciliation/pending`, {
      params: plant_id ? { plant_id } : {},
    }).then(r => r.data),
  run: (plant_id: string) =>
    apiClient.post(`${BASE}/reconciliation/run`, null, {
      params: { plant_id },
    }).then(r => r.data),
  forceClose: (laydown_id: string, note?: string) =>
    apiClient.post(`${BASE}/reconciliation/${laydown_id}/force-close`, {
      note: note ?? null,
    }).then(r => r.data),
}


// ── Masters ───────────────────────────────────────────────────────────────────
export const blowroomLineApi = {
  list: (plant_id?: string) =>
    apiClient.get(`${BASE}/blowroom-lines`, { params: plant_id ? { plant_id } : {} }).then(r => r.data),
  create: (data: Record<string, unknown>) => apiClient.post(`${BASE}/blowroom-lines`, data).then(r => r.data),
  update: (id: string, data: Record<string, unknown>) => apiClient.patch(`${BASE}/blowroom-lines/${id}`, data).then(r => r.data),
}

// ── Phase BM2: HVI-Driven Bale Selection ─────────────────────────────────────
// Endpoints mounted at /api/v1 (no /bale-mgmt prefix on these).

export type SuggestLaydownPayload = {
  plant_id: string
  yarn_recipe_id: string
  quality_tier_id: string
  target_bales: number
  target_kg?: number | null
  selection_mode?: 'INDIVIDUAL_BALE' | 'LOT_WITH_HVI'
  properties?: string[]
  grouping_count?: number
  cotton_variety_filter?: string | null
  crop_year_filter?: number | null
  fifo_tiebreak?: boolean
  blowroom_line_id?: string | null
  previous_laydown_id?: string | null
  // Phase 3B-2: within-cell objective.
  mode?: 'quality' | 'cost' | 'balanced'
}

export const baleSelectionApi = {
  // Quality tiers (3 seeded global: PREMIUM_EXPORT, COMFORTABLE, COARSE)
  listTiers: (plant_id?: string) =>
    apiClient.get('/quality-tiers',
      { params: plant_id ? { plant_id, only_active: true } : { only_active: true } }
    ).then(r => r.data),

  // Per-count usage standards (4 seeded global: 30s combed compact, 30s carded, 24s, 20s)
  listCountStandards: (plant_id?: string) =>
    apiClient.get('/count-quality-standards',
      { params: plant_id ? { plant_id, only_active: true } : { only_active: true } }
    ).then(r => r.data),

  // The matrix algorithm — main floor entry point
  suggest: (data: SuggestLaydownPayload) =>
    apiClient.post('/bale-selection/suggest', data).then(r => r.data),

  // Lifecycle
  getRun: (run_id: string) =>
    apiClient.get(`/bale-selection/runs/${run_id}`).then(r => r.data),
  listRuns: (params?: Record<string, unknown>) =>
    apiClient.get('/bale-selection/runs', { params }).then(r => r.data),
  approve: (run_id: string, data: { override_reason?: string; laydown_plan_id?: string }) =>
    apiClient.post(`/bale-selection/runs/${run_id}/approve`, data).then(r => r.data),
  substitute: (run_id: string, old_bale_id: string, new_bale_id: string) =>
    apiClient.post(`/bale-selection/runs/${run_id}/substitute`,
      { old_bale_id, new_bale_id }
    ).then(r => r.data),

  // Admin utility — recompute bale.quality_group across approved bales
  backfillQualityGroups: (plant_id?: string) =>
    apiClient.post('/bale-selection/backfill-quality-groups',
      plant_id ? { plant_id } : { plant_id: null }
    ).then(r => r.data),

  // Drift dashboard (Phase 2 will use this)
  driftHistory: (plant_id: string, blowroom_line_id?: string, limit = 5) =>
    apiClient.get('/bale-selection/drift-history',
      { params: blowroom_line_id ? { plant_id, blowroom_line_id, limit } : { plant_id, limit } }
    ).then(r => r.data),

  // Phase 2 — Quality Posture Dashboard (rolling-window aggregation)
  postureSummary: (params: {
    plant_id: string
    days?: number
    only_approved?: boolean
    yarn_recipe_id?: string
  }) =>
    apiClient.get('/bale-selection/posture-summary', { params }).then(r => r.data),

  // Phase 2 — Floor Layout swimlanes (group by supplier + quality_group)
  floorMap: (plant_id: string, include_pending = true) =>
    apiClient.get('/bale-selection/floor-map',
      { params: { plant_id, include_pending } }
    ).then(r => r.data),
}

// ── Phase 2 Sprint B — Cotton Grade Master (E1/E2/A/B/C/D/R) ────────────────
export const cottonGradeApi = {
  list: (only_active = true) =>
    apiClient.get('/cotton-grades', { params: { only_active } }).then(r => r.data),
  create: (data: Record<string, unknown>) =>
    apiClient.post('/cotton-grades', data).then(r => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/cotton-grades/${id}`, data).then(r => r.data),
  backfill: (plant_id?: string) =>
    apiClient.post('/cotton-grades/backfill', null,
      { params: plant_id ? { plant_id } : {} }
    ).then(r => r.data),
}

// ── Phase 2 Sprint C — Mixing Recipe Master + recipe-driven selector ────────
export const mixingRecipeApi = {
  list: (params?: { plant_id?: string; yarn_type?: string; only_active?: boolean }) =>
    apiClient.get('/mixing-recipes', { params }).then(r => r.data),
  create: (data: Record<string, unknown>) =>
    apiClient.post('/mixing-recipes', data).then(r => r.data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/mixing-recipes/${id}`, data).then(r => r.data),
  // Recipe-driven alternative to baleSelectionApi.suggest
  suggestByRecipe: (data: {
    plant_id: string
    yarn_recipe_id: string
    quality_tier_id: string
    target_bales: number
    blowroom_line_id?: string
    cotton_variety_filter?: string
    crop_year_filter?: number
    previous_laydown_id?: string
  }) =>
    apiClient.post('/bale-selection/suggest-by-recipe', data).then(r => r.data),
}

// ── Phase 3A — Yarn Test Results (feedback loop) ─────────────────────────────
export const yarnTestApi = {
  list: (params?: {
    plant_id?: string
    fg_lot_id?: string
    laydown_plan_id?: string
    days?: number
    limit?: number
  }) => apiClient.get('/yarn-tests', { params }).then(r => r.data),
  get: (id: string) => apiClient.get(`/yarn-tests/${id}`).then(r => r.data),
  create: (data: Record<string, unknown>) =>
    apiClient.post('/yarn-tests', data).then(r => r.data),
  // Mill Lot performance scorecard — weighted-avg quality across all yarn
  // tests downstream of a given Mill Lot.
  millLotPerformance: (lotId: string) =>
    apiClient.get(`/mill-lots/${lotId}/performance`).then(r => r.data),
  // Top-N Mill Lots by avg quality over a rolling window
  topLots: (plant_id: string, days = 90, limit = 20) =>
    apiClient.get('/yarn-tests/top-lots',
      { params: { plant_id, days, limit } }
    ).then(r => r.data),
}

// ── Phase 3A — Bale Ageing Stats ─────────────────────────────────────────────
export const baleAgeingApi = {
  stats: (plant_id: string) =>
    apiClient.get('/bales/ageing-stats',
      { params: { plant_id } }
    ).then(r => r.data),
}

// ── Phase 3B — Effective cost backfill + multi-option suggest ────────────────
export const costApi = {
  backfillEffectiveCost: (plant_id?: string) =>
    apiClient.post('/bales/backfill-effective-cost', null,
      { params: plant_id ? { plant_id } : {} }
    ).then(r => r.data),
}

// Phase 3B-2: 3-option preview (Quality / Cost / Balanced) without persisting.
// Operator picks one in the UI, then calls baleSelectionApi.suggest with the
// chosen mode to actually save the run.
export const multiOptionApi = {
  suggestOptions: (data: {
    plant_id: string
    yarn_recipe_id: string
    quality_tier_id: string
    target_bales: number
    properties?: string[]
    grouping_count?: number
    cotton_variety_filter?: string
    crop_year_filter?: number
    blowroom_line_id?: string
  }) =>
    apiClient.post('/bale-selection/suggest-options', {
      properties: ['sci', 'mic'],
      grouping_count: 5,
      ...data,
    }).then(r => r.data),
}
